import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Iconify from 'src/components/Iconify';
import { CHATS_ARQUIVADAS_DESIGN as D, getCorDivisao } from './ChatsArquivadas.design';

const CHAVE_RECOLHIDO = 'arquivoSidebarRecolhido';

export default function DivisoesSidebar({
    divisoes,
    totalArquivadas,
    divisaoSelecionada,
    podeGerenciar,
    desarquivarAuto,
    onSelecionar,
    onNovaDivisao,
    onReordenar
}) {
    const { t } = useTranslation('chat');
    const [busca, setBusca] = useState('');
    // Persistido: quem recolhe quer o board largo, e não quer refazer isso a
    // cada visita — mesmo comportamento do layoutMode do app.
    const [recolhido, setRecolhido] = useState(
        () => localStorage.getItem(CHAVE_RECOLHIDO) === '1'
    );

    const alternarRecolhido = () => {
        setRecolhido((atual) => {
            localStorage.setItem(CHAVE_RECOLHIDO, atual ? '0' : '1');
            return !atual;
        });
    };

    const divisoesFiltradas = useMemo(() => {
        if (!busca.trim()) return divisoes;
        const termo = busca.trim().toLowerCase();
        return divisoes.filter((divisao) => (divisao.nome || '').toLowerCase().includes(termo));
    }, [divisoes, busca]);

    const handleDragEnd = (resultado) => {
        const { destination, source } = resultado;
        if (!destination || destination.index === source.index) return;
        // Reordenar com busca ativa moveria o item na lista errada.
        if (busca.trim()) return;

        const nova = Array.from(divisoes);
        const [movida] = nova.splice(source.index, 1);
        nova.splice(destination.index, 0, movida);
        onReordenar(nova);
    };

    if (recolhido) {
        return (
            <aside className={D.sidebar.containerRecolhido}>
                <div className="flex items-center justify-center pt-3 pb-1">
                    <button
                        type="button"
                        className={D.sidebar.toggle}
                        onClick={alternarRecolhido}
                        title={t('archive.sidebar.expand', 'Expandir divisões')}
                    >
                        <Iconify icon="eva:chevron-right-outline" width={16} />
                    </button>
                </div>

                <div className={D.sidebar.railList}>
                    <div
                        onClick={() => onSelecionar(null)}
                        title={t('archive.sidebar.allArchivedTitle', 'Todos arquivados ({{count}})', { count: totalArquivadas })}
                        className={`${D.sidebar.railItem} ${!divisaoSelecionada ? D.sidebar.railItemActive : D.sidebar.railItemIdle}`}
                    >
                        <Iconify icon="eva:archive-outline" width={16} className="text-[#7C3AED]" />
                        {totalArquivadas > 0 && (
                            <span className={D.sidebar.railBadge}>
                                {totalArquivadas > 99 ? '99+' : totalArquivadas}
                            </span>
                        )}
                    </div>

                    {divisoes.map((divisao) => {
                        const cor = getCorDivisao(divisao.cor_hex);
                        const ativa = `${divisaoSelecionada}` === `${divisao._id}`;
                        return (
                            <div
                                key={`${divisao._id}`}
                                onClick={() => onSelecionar(`${divisao._id}`)}
                                title={t('archive.sidebar.divisionTitle', '{{name}} ({{count}})', { name: divisao.nome, count: divisao.total || 0 })}
                                className={`${D.sidebar.railItem} ${ativa ? D.sidebar.railItemActive : D.sidebar.railItemIdle}`}
                            >
                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${cor.bg}`}>
                                    <Iconify
                                        icon={divisao.icone || 'eva:folder-outline'}
                                        width={15}
                                        className={cor.text}
                                    />
                                </div>
                                {(divisao.total || 0) > 0 && (
                                    <span className={D.sidebar.railBadge}>
                                        {divisao.total > 99 ? '99+' : divisao.total}
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {podeGerenciar && (
                    <div className="flex items-center justify-center py-3">
                        <button
                            type="button"
                            className={D.sidebar.addButton}
                            onClick={onNovaDivisao}
                            title={t('archive.sidebar.newDivision', 'Nova divisão')}
                        >
                            <Iconify icon="eva:plus-outline" width={14} />
                        </button>
                    </div>
                )}
            </aside>
        );
    }

    return (
        <aside className={D.sidebar.container}>
            <div className={D.sidebar.toggleRow}>
                <span className={D.sidebar.sectionLabel}>{t('archive.sidebar.title', 'Arquivadas')}</span>
                <button
                    type="button"
                    className={D.sidebar.toggle}
                    onClick={alternarRecolhido}
                    title={t('archive.sidebar.collapse', 'Recolher divisões')}
                >
                    <Iconify icon="eva:chevron-left-outline" width={16} />
                </button>
            </div>

            <div className={D.sidebar.search}>
                <div className="relative">
                    <Iconify
                        icon="eva:search-outline"
                        width={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                        type="text"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        placeholder={t('archive.sidebar.searchPlaceholder', 'Pesquisar conversas arquivadas...')}
                        className={D.sidebar.searchInput}
                    />
                </div>
            </div>

            <div className={D.sidebar.sectionHeader}>
                <span className={D.sidebar.sectionLabel}>{t('archive.sidebar.divisionsLabel', 'Divisões')}</span>
                {podeGerenciar && (
                    <button type="button" className={D.sidebar.addButton} onClick={onNovaDivisao}>
                        <Iconify icon="eva:plus-outline" width={14} />
                    </button>
                )}
            </div>

            <div className={D.sidebar.list}>
                <button
                    type="button"
                    onClick={() => onSelecionar(null)}
                    className={`${D.sidebar.item} ${!divisaoSelecionada ? D.sidebar.itemActive : D.sidebar.itemIdle}`}
                >
                    <div className="w-6 h-6 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                        <Iconify icon="eva:archive-outline" width={14} className="text-[#7C3AED]" />
                    </div>
                    <span className="truncate">{t('archive.sidebar.allArchived', 'Todos arquivados')}</span>
                    <span className={D.sidebar.itemCount}>{totalArquivadas}</span>
                </button>

                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="divisoes-sidebar" type="divisao">
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-0.5">
                                {divisoesFiltradas.map((divisao, index) => {
                                    const cor = getCorDivisao(divisao.cor_hex);
                                    const ativa = `${divisaoSelecionada}` === `${divisao._id}`;

                                    return (
                                        <Draggable
                                            key={`${divisao._id}`}
                                            draggableId={`sidebar-${divisao._id}`}
                                            index={index}
                                            isDragDisabled={!podeGerenciar || !!busca.trim()}
                                        >
                                            {(dragProvided) => (
                                                <div
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    onClick={() => onSelecionar(`${divisao._id}`)}
                                                    className={`${D.sidebar.item} ${ativa ? D.sidebar.itemActive : D.sidebar.itemIdle}`}
                                                >
                                                    <span {...dragProvided.dragHandleProps} className={D.sidebar.itemHandle}>
                                                        <Iconify icon="eva:menu-outline" width={12} />
                                                    </span>
                                                    <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${cor.bg}`}>
                                                        <Iconify
                                                            icon={divisao.icone || 'eva:folder-outline'}
                                                            width={14}
                                                            className={cor.text}
                                                        />
                                                    </div>
                                                    <span className="truncate">{divisao.nome}</span>
                                                    <span className={D.sidebar.itemCount}>{divisao.total || 0}</span>
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>

            {podeGerenciar && (
                <button type="button" className={D.sidebar.newDivision} onClick={onNovaDivisao}>
                    <Iconify icon="eva:plus-outline" width={14} />
                    {t('archive.sidebar.newDivision', 'Nova divisão')}
                </button>
            )}

            <div className={D.sidebar.infoCard}>
                <div className={D.sidebar.infoTitle}>
                    <Iconify icon="eva:info-outline" width={14} className="text-[#7C3AED]" />
                    {t('archive.sidebar.infoTitle', 'Como funciona?')}
                </div>
                <div className={D.sidebar.infoItem}>
                    <Iconify icon="eva:archive-outline" width={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>{t('archive.sidebar.infoArchiveHint', 'A conversa sai do atendimento e o bot para de responder enquanto ela estiver arquivada.')}</span>
                </div>
                <div className={D.sidebar.infoItem}>
                    <Iconify icon="eva:message-circle-outline" width={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>
                        {desarquivarAuto
                            ? t('archive.sidebar.infoAutoUnarchive', 'Ao receber nova mensagem, a conversa é desarquivada automaticamente e volta para Atendimentos.')
                            : t('archive.sidebar.infoManualUnarchive', 'Conversas permanecem arquivadas mesmo recebendo novas mensagens — você desarquiva manualmente.')}
                    </span>
                </div>
                <div className={D.sidebar.infoItem}>
                    <Iconify icon="eva:funnel-outline" width={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <span>{t('archive.sidebar.infoOrganize', 'Organize suas conversas em divisões para acompanhar seus estágios e processos.')}</span>
                </div>
            </div>
        </aside>
    );
}
